import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";

interface BlogPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  submission: {
    title: string;
    content_html: string;
    featured_image_url?: string;
    word_count: number;
    tags?: string[];
    meta_description?: string;
  } | null;
}

export const BlogPreviewModal = ({ isOpen, onClose, submission }: BlogPreviewModalProps) => {
  const { t } = useTranslation();
  
  if (!submission) return null;

  const sanitizedHtml = DOMPurify.sanitize(submission.content_html);
  const readingTime = Math.ceil(submission.word_count / 200);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {t("admin.creator.previewBlog")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Featured Image */}
          {submission.featured_image_url && (
            <img 
              src={submission.featured_image_url} 
              alt={submission.title}
              className="w-full h-64 object-cover border-[3px] border-dark"
            />
          )}

          {/* Title */}
          <h1 className="font-heading text-3xl">{submission.title}</h1>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground pb-4 border-b-[3px] border-dark">
            <span>📊 {submission.word_count} {t("admin.creator.previewLabels.words")}</span>
            <span>⏱️ ~{readingTime} {t("admin.creator.previewLabels.readingTime")}</span>
            {submission.tags && submission.tags.length > 0 && (
              <span>🏷️ {submission.tags.join(", ")}</span>
            )}
          </div>

          {/* Meta Description */}
          {submission.meta_description && (
            <div className="bg-muted/50 p-4 border-l-4 border-primary">
              <p className="text-sm italic">{submission.meta_description}</p>
            </div>
          )}

          {/* Content */}
          <div 
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
